import { useEffect, useState } from 'react';
import { Plus, Pencil, Users } from 'lucide-react';
import { clientesApi, empleadosApi } from '../api/endpoints';
import { Modal } from '../components/ui/Modal';
import { Spinner } from '../components/ui/Spinner';
import { SearchInput } from '../components/ui/SearchInput';
import { EmptyState } from '../components/ui/EmptyState';
import { Alert } from '../components/ui/Alert';
import { useAuthStore } from '../auth/authStore';

const empty = { clienteId: '', nombreCompleto: '', puesto: '', email: '', telefono: '', departamento: '' };

export function Empleados() {
  const usuario = useAuthStore((s) => s.usuario);
  const isAdmin = usuario?.rol === 'Admin';
  const [data, setData] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const params = {};
      if (filterCliente) params.clienteId = filterCliente;
      setData(await empleadosApi.list(params));
    } catch (e) { setError(e?.response?.data?.error || 'Error al cargar empleados'); }
  };
  useEffect(() => { (async () => { setClientes(await clientesApi.list()); })(); }, []);
  useEffect(() => { load(); }, [filterCliente]);

  const openCreate = () => { setEditing(null); setForm({ ...empty, clienteId: clientes[0]?.id || '' }); setModalOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ clienteId: e.clienteId, nombreCompleto: e.nombreCompleto, puesto: e.puesto, email: e.email, telefono: e.telefono, departamento: e.departamento }); setModalOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await empleadosApi.update(editing.id, { ...form, activo: editing.activo });
      else await empleadosApi.create({ ...form, clienteId: Number(form.clienteId) });
      setModalOpen(false);
      await load();
    } catch (e) { setError(e?.response?.data?.error || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const clienteMap = Object.fromEntries(clientes.map(c => [c.id, c.nombreComercial]));
  const filtered = (data ?? []).filter(e => !search || e.nombreCompleto.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-3 sm:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 flex-wrap">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar empleado..." className="max-w-xs" />
          <select value={filterCliente} onChange={(e) => setFilterCliente(e.target.value)} className="px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white">
            <option value="">Todos los clientes</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nombreComercial}</option>)}
          </select>
        </div>
        {isAdmin && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-lg shrink-0">
            <Plus size={16} /> <span className="hidden sm:inline">Nuevo Empleado</span>
          </button>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {data === null ? (
        <div className="flex justify-center py-12"><Spinner size={32} /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={Users} title="Sin empleados" description="No hay empleados registrados." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((e) => (
            <div key={e.id} className="bg-panel border border-border rounded-xl p-4 hover:border-cyan-500/40 transition group">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-white truncate">{e.nombreCompleto}</h3>
                  <p className="text-xs text-muted truncate">{clienteMap[e.clienteId] || '—'}</p>
                </div>
                {isAdmin && (
                  <button onClick={() => openEdit(e)} className="p-1.5 rounded text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 shrink-0 ml-2 md:opacity-0 md:group-hover:opacity-100" title="Editar">
                    <Pencil size={14} />
                  </button>
                )}
              </div>
              <div className="text-xs text-slate-300 space-y-1 mb-3">
                {e.puesto && <div className="truncate">📌 {e.puesto}{e.departamento && ` · ${e.departamento}`}</div>}
                {e.email && <div className="truncate">📧 {e.email}</div>}
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-[10px] text-muted truncate">{clienteMap[e.clienteId] || ''}</span>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${e.activo ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
                  {e.activo ? 'ACTIVO' : 'INACTIVO'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Editar Empleado' : 'Nuevo Empleado'}>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">CLIENTE *</label>
            <select value={form.clienteId} onChange={(e) => setForm({ ...form, clienteId: e.target.value })} className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white">
              {clientes.map(c => <option key={c.id} value={c.id}>{c.nombreComercial}</option>)}
            </select>
          </div>
          <Field label="Nombre completo *" value={form.nombreCompleto} onChange={(v) => setForm({ ...form, nombreCompleto: v })} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Puesto" value={form.puesto} onChange={(v) => setForm({ ...form, puesto: v })} />
            <Field label="Departamento" value={form.departamento} onChange={(v) => setForm({ ...form, departamento: v })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Teléfono" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-border">
          <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded border border-border text-slate-300 hover:bg-panel-2 text-sm">Cancelar</button>
          <button onClick={save} disabled={saving || !form.nombreCompleto || !form.clienteId} className="px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold disabled:opacity-50">
            {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </Modal>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-300 mb-1.5 tracking-wide">{label}</label>
      <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white focus:outline-none focus:border-cyan-500" />
    </div>
  );
}
